/**
 * Hook que construye la barra IndexFilters y las preferencias de columnas para listas estándar.
 * Ahora que el filtrado de la aplicación es síncrono (en memoria local), este componente
 * simplificado renderiza <IndexFilters> de forma directa y fluida.
 */

import React, { useState, useEffect, useCallback, useMemo, type ReactNode } from 'react';
import { IndexFilters, IndexFiltersMode, type TabProps, type SortButtonChoice } from '@/lib/polaris';
import {
  loadListColumnPreferences,
  saveListColumnPreferences,
  type ListColumnPreferences,
} from '@/lib/listColumnPreferences';
import type { ListViewConfig } from '@/types/listView.types';

export interface StandardListFiltersViewProps {
  activeView: { label: string };
  selectedTabIndex: number;
  handleSwitchView: (index: number) => void;
  handleCancelFilter: () => void;
  handleSaveView: () => Promise<boolean>;
  createViewWithName: (name: string) => void;
  hasUnsavedFilterChanges: boolean;
  indexFiltersTabs: TabProps[];
}

export interface StandardListFiltersOptions<Item extends object = Record<string, unknown>> {
  listId: string;
  tenantId: string | undefined;
  config: ListViewConfig<Item>;
  views: StandardListFiltersViewProps;
  searchValue: string;
  setSearchValue: (value: string) => void;
  queryPlaceholder: string;
  /** Array de filtros para IndexFilters (key, label, shortcut?, pinned?, filter: ReactNode) */
  filters: Array<{
    key: string;
    label: string;
    shortcut?: boolean;
    pinned?: boolean;
    filter: ReactNode;
  }>;
  appliedFilters: Array<{ key: string; label: string; onRemove: () => void }>;
  onClearAll: () => void;
  sortOptions: SortButtonChoice[];
  sortSelected: string[];
  onSort: (value: string[]) => void;
  mode: IndexFiltersMode;
  setMode: (mode: IndexFiltersMode) => void;
  /** Si false, oculta el botón "Editar columnas" (p. ej. en vista grid donde no hay columnas de tabla). Por defecto true. */
  showEditColumnsButton?: boolean;
  /** Si true, suprime el "Guardar como" nativo de Polaris en la vista de sistema (cuando la página
   *  ofrece su propio botón de guardar, p. ej. al agrupar con nombre pre-rellenado). Por defecto false. */
  suppressSaveAs?: boolean;
}

export interface StandardListFiltersResult {
  filterControl: ReactNode;
  columnOrder: string[];
  hiddenColumnKeys: string[];
  editColumnsMode: boolean;
  editingColumnPrefs: ListColumnPreferences | null;
  onColumnOrderChange: (order: string[]) => void;
  onToggleColumnVisibility: (key: string) => void;
}

export function useStandardListFilters<Item extends object = Record<string, unknown>>(
  options: StandardListFiltersOptions<Item>
): StandardListFiltersResult {
  const {
    listId,
    tenantId,
    config,
    views,
    searchValue,
    setSearchValue,
    queryPlaceholder,
    filters,
    appliedFilters,
    onClearAll,
    sortOptions,
    sortSelected,
    onSort,
    mode,
    setMode,
    showEditColumnsButton: showEditColumnsButtonOption = true,
    suppressSaveAs = false,
  } = options;

  const [columnPreferences, setColumnPreferences] = useState<ListColumnPreferences | null>(() =>
    loadListColumnPreferences(listId, tenantId ?? undefined)
  );
  const [editingColumnPrefs, setEditingColumnPrefs] = useState<ListColumnPreferences | null>(null);

  useEffect(() => {
    setColumnPreferences(loadListColumnPreferences(listId, tenantId ?? undefined));
  }, [listId, tenantId]);

  const effectiveColumnOrder = useMemo(() => {
    const configKeys = config.columns.map((c) => c.key);
    const saved = columnPreferences?.columnOrder;
    if (!saved?.length) return configKeys;
    // Incluir columnas nuevas: orden guardado + cualquier key de config que no estuviera guardada (aparecen al final)
    const savedSet = new Set(saved);
    const newKeys = configKeys.filter((k) => !savedSet.has(k));
    const keptSaved = saved.filter((k) => configKeys.includes(k));
    return [...keptSaved, ...newKeys];
  }, [columnPreferences?.columnOrder, config.columns]);
  const effectiveHiddenKeys = columnPreferences?.hiddenColumnKeys ?? (config.defaultHiddenColumnKeys ?? []);

  const tableColumnOrder =
    mode === IndexFiltersMode.EditingColumns && editingColumnPrefs
      ? editingColumnPrefs.columnOrder
      : effectiveColumnOrder;
  const tableHiddenKeys =
    mode === IndexFiltersMode.EditingColumns && editingColumnPrefs
      ? editingColumnPrefs.hiddenColumnKeys
      : effectiveHiddenKeys;

  const primaryAction = useMemo<React.ComponentProps<typeof IndexFilters>['primaryAction']>(() => {
    if (mode === IndexFiltersMode.EditingColumns) {
      return {
        type: 'save' as const,
        onAction: async () => {
          if (editingColumnPrefs) {
            setColumnPreferences(editingColumnPrefs);
            if (tenantId) saveListColumnPreferences(listId, tenantId, editingColumnPrefs);
            setEditingColumnPrefs(null);
          }
          setMode(IndexFiltersMode.Default);
          return true;
        },
        disabled: false,
        loading: false,
      };
    }
    if (views.selectedTabIndex === 0) {
      // La página puede ofrecer su propio "Guardar como" (p. ej. al agrupar, con nombre
      // pre-rellenado): en ese caso suprimimos el nativo para no duplicar el botón.
      if (suppressSaveAs) return undefined;
      return {
        type: 'save-as' as const,
        onAction: async (name?: string) => {
          views.createViewWithName((name ?? '').trim() || 'Vista nueva');
          return true;
        },
        disabled: false,
        loading: false,
      };
    }
    return {
      type: 'save' as const,
      onAction: views.handleSaveView,
      disabled: !views.hasUnsavedFilterChanges,
      loading: false,
    };
  }, [mode, views, editingColumnPrefs, tenantId, listId, setMode, suppressSaveAs]);

  const cancelAction = useMemo<React.ComponentProps<typeof IndexFilters>['cancelAction']>(() => {
    if (mode === IndexFiltersMode.EditingColumns) {
      return {
        onAction: () => {
          setEditingColumnPrefs(null);
          setMode(IndexFiltersMode.Default);
        },
        disabled: false,
        loading: false,
      };
    }
    return {
      onAction: views.handleCancelFilter,
      disabled: false,
      loading: false,
    };
  }, [mode, views, setMode]);

  const handleColumnOrderChange = useCallback((order: string[]) => {
    setEditingColumnPrefs((prev) => (prev ? { ...prev, columnOrder: order } : null));
  }, []);

  const handleToggleColumnVisibility = useCallback((key: string) => {
    setEditingColumnPrefs((prev) => {
      if (!prev) return null;
      const next = prev.hiddenColumnKeys.includes(key)
        ? prev.hiddenColumnKeys.filter((h) => h !== key)
        : [...prev.hiddenColumnKeys, key];
      return { ...prev, hiddenColumnKeys: next };
    });
  }, []);

  const filterControl = useMemo(
    () => (
      <IndexFilters
        filters={filters}
        appliedFilters={appliedFilters}
        tabs={views.indexFiltersTabs}
        selected={views.selectedTabIndex >= 0 ? views.selectedTabIndex : 0}
        onSelect={views.handleSwitchView}
        primaryAction={primaryAction}
        cancelAction={cancelAction}
        queryValue={searchValue}
        queryPlaceholder={queryPlaceholder}
        sortSelected={sortSelected}
        onClearAll={onClearAll}
        onSort={onSort}
        onCreateNewView={async (name?: string) => {
          views.createViewWithName(name?.trim() || 'Vista nueva');
          return true;
        }}
        mode={mode}
        setMode={setMode}
        onQueryChange={setSearchValue}
        onQueryClear={() => setSearchValue('')}
        sortOptions={sortOptions}
        canCreateNewView
        showEditColumnsButton={showEditColumnsButtonOption}
        onEditStart={(editMode) => {
          if (editMode === IndexFiltersMode.EditingColumns) {
            setEditingColumnPrefs(
              columnPreferences ?? {
                columnOrder: config.columns.map((c) => c.key),
                hiddenColumnKeys: config.defaultHiddenColumnKeys ?? [],
              }
            );
          }
        }}
        filteringAccessibilityLabel="Buscar y filtrar"
        filteringAccessibilityTooltip="Buscar y filtrar (F)"
      />
    ),
    [
      filters,
      appliedFilters,
      views,
      primaryAction,
      cancelAction,
      searchValue,
      queryPlaceholder,
      sortSelected,
      onClearAll,
      onSort,
      mode,
      setMode,
      setSearchValue,
      sortOptions,
      showEditColumnsButtonOption,
      editingColumnPrefs,
      columnPreferences,
      config.columns,
    ]
  );

  return {
    filterControl,
    columnOrder: tableColumnOrder,
    hiddenColumnKeys: tableHiddenKeys,
    editColumnsMode: mode === IndexFiltersMode.EditingColumns && !!editingColumnPrefs,
    editingColumnPrefs,
    onColumnOrderChange: handleColumnOrderChange,
    onToggleColumnVisibility: handleToggleColumnVisibility,
  };
}
